<template>
  <div class="Component-reviewer-map">
    <div id="pilotNetworkMap"></div>
    <div class="sidebar-list">
      <div v-show="showDetail" class="detail-pop">
        <div class="close-con">
          <span class=" material-icons" @click="closeDetailPop">
            close
          </span>
        </div>
        <div>
          <div class="title">
            <div>
              {{ $t("Address") }}: {{ selectedReviewer.review_street }},
              {{ selectedReviewer.review_city }},
              {{ selectedReviewer.review_province }}
            </div>
          </div>
          <div v-if="selectedReviewer.price" style="margin: 8px 0 16px 0;">
            {{ $t("Price") }}: ${{ (selectedReviewer.price / 100).toFixed(2) }}
          </div>
          <md-button
            v-if="$route.path === '/flight-review/map'"
            class="md-primary"
            @click="bookReview(selectedReviewer)"
            >{{ $t("Book") }}</md-button
          >
        </div>
      </div>
      <md-field>
        <md-input
          type="search"
          class="mb-3"
          clearable
          style="padding-left: 20px;"
          :placeholder="$t('Search')"
          v-model="searchQuery"
        ></md-input>
      </md-field>
      <md-list class="md-double-line">
        <md-list-item
          class="list-item"
          v-for="(item, idx) in reviewList"
          :key="idx"
          @click="flyTo(item)"
        >
          <div class="md-list-item-text">
            <div>
              {{ item.review_street }}, {{ item.review_city }},
              {{ item.review_province }}
            </div>
            <div v-if="item.price" style="margin-top: 8px;">
              {{ $t("Price") }}: ${{ (item.price / 100).toFixed(2) }}
            </div>
          </div>

          <div class="md-list-action">
            <div class="material-icons">
              arrow_forward_ios
            </div>
          </div>
        </md-list-item>
      </md-list>
    </div>
    <Notifications />
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "mapbox-gl/dist/mapbox-gl.css";
import { mapboxAccessToken } from "@/const";
import { getReviewerList } from "@/services/flightReview.service.js";
import { coordinatesGeocoder, debounce } from "@/helpers/utils";
import { mapFields } from "vuex-map-fields";
import swal from "sweetalert2";

export default {
  name: "PilotNetwork",
  props: {
    bookingCbfn: {
      type: Function,
      default: () => {}
    },
    reviewerNumberCbfn: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      latitude: 43.4723,
      longitude: -80.5449,
      selectedReviewer: {},
      showDetail: false,
      searchQuery: "",
      debouncedSearch: debounce(this.getReviewers, 300),
      reviewList: []
    };
  },
  computed: {
    ...mapFields("auth", ["loggedOn"]),
    ...mapFields("flightReview", ["booking"])
  },
  mounted() {
    this.loadMap(); // loading first, getCurrentPosition callbackFN is very slow
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        this.longitude = pos.coords.longitude;
        this.latitude = pos.coords.latitude;
        this.loadMap();
      });
    }
  },
  methods: {
    async loadMap() {
      var self = this;
      mapboxgl.accessToken = mapboxAccessToken;
      this.map = new mapboxgl.Map({
        container: "pilotNetworkMap",
        style: "mapbox://styles/mapbox/satellite-streets-v11",
        center: [this.longitude, this.latitude],
        zoom: 10
      });

      const nav = new mapboxgl.NavigationControl({
        showCompass: false
      });
      this.map.addControl(nav, "top-left");

      var geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        localGeocoder: coordinatesGeocoder,
        countries: "ca",
        marker: false,
        mapboxgl: mapboxgl,
        language:
          window.localStorage.getItem("locale") === "fr" ? "fr-CA" : "en-US",
        placeholder: this.$t("Address")
      });
      this.map.addControl(geocoder);

      const { data } = await getReviewerList();
      const reviewList = JSON.parse(data.data);

      this.reviewList = reviewList;
      this.reviewerNumberCbfn && this.reviewerNumberCbfn(reviewList.length);
      reviewList.forEach(item => {
        const customMarker = document.createElement("div");
        customMarker.className = "custom-marker";
        if (item.longitude && item.latitude) {
          new mapboxgl.Marker(customMarker)
            .setLngLat([item.longitude, item.latitude])
            .addTo(this.map);
        }

        customMarker.addEventListener("click", function() {
          self.flyTo(item);
        });

        if (this.booking.reviewerId === item._id.$oid) {
          this.selectedReviewer = item;
          if (this.bookingCbfn) this.bookingCbfn(item);
          this.showDetail = true;
        }
      });
    },
    flyTo(item) {
      if (
        !item.longitude ||
        !item.latitude ||
        (item.flight_schools || []).length === 0
      ) {
        this.$notify({
          message: this.$t("Reviewer address or school does not exist."),
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "warning",
          timeout: 3000
        });
        return;
      }
      this.selectedReviewer = item;
      if (this.bookingCbfn) this.bookingCbfn(item);
      this.showDetail = true;
      this.map.flyTo({
        center: [item.longitude, item.latitude],
        zoom: 15,
        essential: true // for animation smooth
      });
    },
    closeDetailPop() {
      this.selectedReviewer = {};
      if (this.bookingCbfn) this.bookingCbfn({});
      this.showDetail = false;
      this.map.setZoom(10);
    },
    async getReviewers(keywords) {
      const { data } = await getReviewerList(keywords);
      this.reviewList = JSON.parse(data.data);
    },
    bookReview(reviewerInfo) {
      if (this.loggedOn) {
        sessionStorage.setItem("reviewerInfo", JSON.stringify(reviewerInfo));
        this.$router.push("/flight-review/booking")
        return
      }
      swal
        .fire({
          text: this.$t("Login required for Booking Flight Review."),
          showCancelButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
          confirmButtonClass: "md-button md-success",
          cancelButtonClass: "md-button",
          confirmButtonText: this.$t("Yes"),
          cancelButtonText: this.$t("Cancel"),
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            sessionStorage.setItem("reviewerInfo", JSON.stringify(reviewerInfo));
            this.$router.push({ path: '/login', query: { loginSuccessTargetPage: '/flight-review/booking' } });
          }
        });
      
    }
  },
  watch: {
    searchQuery(value) {
      this.debouncedSearch(value);
    }
  }
};
</script>

<style lang="scss">
.Component-reviewer-map {
  display: flex;
  height: 60vh;

  #pilotNetworkMap {
    height: 100%;
    flex: 8;
    margin: 0px;
    padding: 0px;
  }

  flex-shrink: 0;

  .sidebar-list {
    position: relative;
    flex: 4;
    padding-left: 16px;

    .md-double-line {
      overflow: auto;
      height: calc(100% - 60px);
    }

    .detail-pop {
      position: absolute;
      left: 0;
      width: 100%;
      top: 0;
      bottom: 0;
      z-index: 10;
      background: #fff;
      padding-left: 16px;

      .close-con {
        cursor: pointer;
        text-align: right;
        margin-top: 10px;
      }
    }
  }
  @media (max-width: 768px) {
    #pilotNetworkMap {
      display: none;
    }

    .sidebar-list {
      width: 100%;
    }
  }

  .custom-marker {
    background-image: url("../../../../public/img/map-marker.png");
    background-size: cover;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    cursor: pointer;
  }

  .list-item {
    cursor: pointer;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }
}
</style>
